import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import useAuth from '../auth/feature/auth';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';
import { HttpFactoryService } from '../common/services';
import { BACKEND_KEYS } from '../common/consts/app-keys.const';

const httpService = new HttpFactoryService().createHttpService();

const INVITE_TOKEN_KEY = 'standby_invite_token';

/** Store a pending invite token so it survives signup/activation/signin flow */
export const storePendingInvite = (token: string) =>
  localStorage.setItem(INVITE_TOKEN_KEY, token);

export const getPendingInvite = () => localStorage.getItem(INVITE_TOKEN_KEY);
export const clearPendingInvite = () => localStorage.removeItem(INVITE_TOKEN_KEY);

export const InvitePage = () => {
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated, isLoading: authLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const hasAttempted = React.useRef(false);

  const redeemMutation = useMutation({
    mutationFn: () => httpService.post(BACKEND_KEYS.REDEEM_INVITE, { token }),
    onSuccess: () => {
      clearPendingInvite();
      checkAuth();
      setTimeout(() => navigate(ROUTER_LINKS.DASHBOARD), 2000);
    },
    onError: () => {
      clearPendingInvite();
    }
  });

  // If not authenticated, store token and redirect to signup
  React.useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated && token) {
      storePendingInvite(token);
      navigate(ROUTER_LINKS.SIGNUP, { replace: true });
    }
  }, [isAuthenticated, authLoading, token, navigate]);

  // Auto-redeem when authenticated
  React.useEffect(() => {
    if (authLoading || !isAuthenticated || hasAttempted.current) return;
    hasAttempted.current = true;
    redeemMutation.mutate();
  }, [isAuthenticated, authLoading]);

  const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      return axiosError.response?.data?.message || 'Something went wrong. Please try again.';
    }
    return 'Something went wrong. Please try again.';
  };

  return (
    <div className="max-w-[500px] mx-auto py-[48px] px-[16px]">
      <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] p-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07)] text-center">
        {(authLoading || redeemMutation.isPending) && (
          <>
            <div className="flex justify-center mb-[16px]">
              <div className="w-[32px] h-[32px] border-[3px] border-[#E5E5ED] dark:border-[#333336] border-t-[#0ea5c9] rounded-full animate-spin" />
            </div>
            <h1 className="font-display text-[24px] font-bold text-base-black dark:text-white mb-[8px]">
              Activating Pro...
            </h1>
            <p className="font-body text-[15px] text-gray-900 dark:text-[#A0A0A3]">
              Please wait while we upgrade your account.
            </p>
          </>
        )}

        {redeemMutation.isSuccess && (
          <>
            <div className="flex justify-center mb-[16px]">
              <div className="w-[48px] h-[48px] rounded-full bg-[#ECFDF5] dark:bg-[#052e16] flex items-center justify-center">
                <svg className="w-[24px] h-[24px] text-[#059669]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h1 className="font-display text-[24px] font-bold text-base-black dark:text-white mb-[8px]">
              Welcome to Pro!
            </h1>
            <p className="font-body text-[15px] text-gray-900 dark:text-[#A0A0A3]">
              Your account has been upgraded. Redirecting...
            </p>
          </>
        )}

        {redeemMutation.isError && (
          <>
            <div className="flex justify-center mb-[16px]">
              <div className="w-[48px] h-[48px] rounded-full bg-[#FEF2F2] dark:bg-[#450a0a] flex items-center justify-center">
                <svg className="w-[24px] h-[24px] text-[#D8210C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h1 className="font-display text-[24px] font-bold text-base-black dark:text-white mb-[8px]">
              Invite Error
            </h1>
            <p className="font-body text-[15px] text-[#D8210C] mb-[16px]">
              {getErrorMessage(redeemMutation.error)}
            </p>
            <button
              onClick={() => navigate(ROUTER_LINKS.DASHBOARD)}
              className="h-[44px] px-[24px] bg-base-black dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] rounded-[32px] font-body text-[14px] font-medium hover:bg-[#29292A] dark:hover:bg-[#D8D8DB] transition-all"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default InvitePage;
